export enum SgpFlagMode {
  Normal,
  Advanced,
}

export enum SgpDeviceType {
  App = 0x00,
  Core = 0x01,
  SafetyDocs = 0x04,
}

export enum SgpSupportedDeviceType {
  ARM = 0x21,
  DSPM = 0x22,
  DSPS = 0x23,
  BMS = 0x24,
  PCU = 0x25,
  BDU = 0x26,
  DCDC = 0x27,
  DCDC_ARM = 0x28,
  DCDC_DSP = 0x29,
  FUSE = 0x41,
  AFCI = 0x42,
  MPPT = 0x43,
}

/**
 * 0. Can normally upgrade
 * 1. The file has been downloaded before, and can be resumed at the breakpoint
 * 2. The system is busy now and unable to upgrade
 * 3. System flash exception, unable to upgrade
 * 4. Firmware name error, unable to upgrade
 * 5. File size error, unable to upgrade
 * 6. Device type error, unable to upgrade
 * 7. Battery type error, unable to upgrade (BMS/PCU/BDU)
 * 8. No PV or grid connection, unable to upgrade (BMC/PCU)
 * 9. Battery address not configured, unable to upgrade (BMS/PCU/BDU)
 */
export enum ReturnDownloadCmd {
  Normal = 0x00,
  Resume = 0x01,
  Busy = 0x02,
  FlashError = 0x03,
  FirmwareNameError = 0x04,
  FileSizeError = 0x05,
  DeviceTypeError = 0x06,
  BatteryTypeError = 0x07,
  NoPVOrGridError = 0x08,
  BatteryAddressError = 0x09,
}
export const ReturnDownloadCmdDescription = {
  [ReturnDownloadCmd.Normal]: 'Can normally upgrade',
  [ReturnDownloadCmd.Resume]: 'The file has been downloaded before, and can be resumed at the breakpoint',
  [ReturnDownloadCmd.Busy]: 'The system is busy now and unable to upgrade',
  [ReturnDownloadCmd.FlashError]: 'System flash exception, unable to upgrade',
  [ReturnDownloadCmd.FirmwareNameError]: 'Firmware name error, unable to upgrade',
  [ReturnDownloadCmd.FileSizeError]: 'File size error, unable to upgrade',
  [ReturnDownloadCmd.DeviceTypeError]: 'Device type error, unable to upgrade',
  [ReturnDownloadCmd.BatteryTypeError]: 'Battery type error, unable to upgrade (BMS/PCU/BDU)',
  [ReturnDownloadCmd.NoPVOrGridError]: 'No PV or grid connection, unable to upgrade (BMC/PCU)',
  [ReturnDownloadCmd.BatteryAddressError]: 'Battery address not configured, unable to upgrade (BMS/PCU/BDU)',
};

/**
 * 0. Data reception is normal
 * 1. Offset address error
 * 2. Data length error
 * 3. File storage failed
 * 4. File identification error
 */
export enum ReturnDistributeCmd {
  Normal = 0x00,
  OffsetAddressError = 0x01,
  DataLengthError = 0x02,
  FileStorageError = 0x03,
  FileIdentificationError = 0x04,
}
export const ReturnDistributeCmdDescription = {
  [ReturnDistributeCmd.Normal]: 'Data reception is normal',
  [ReturnDistributeCmd.OffsetAddressError]: 'Offset address error',
  [ReturnDistributeCmd.DataLengthError]: 'Data length error',
  [ReturnDistributeCmd.FileStorageError]: 'File storage failed',
  [ReturnDistributeCmd.FileIdentificationError]: 'File identification error',
};

/**
 * 0. Downloaded files are correct
 * 1. File validation failed
 * 2. File identification error
 * 3. Types of firmware and local chip do not match
 */
export enum ReturnValidateCmd {
  Normal = 0x00,
  FileValidationFailed = 0x01,
  FileIdentificationError = 0x02,
  FirmwareAndLocalChipNotMatch = 0x03,
}
export const ReturnValidateCmdDescription = {
  [ReturnValidateCmd.Normal]: 'Downloaded files are correct',
  [ReturnValidateCmd.FileValidationFailed]: 'File validation failed',
  [ReturnValidateCmd.FileIdentificationError]: 'File identification error',
  [ReturnValidateCmd.FirmwareAndLocalChipNotMatch]: 'Types of firmware and local chip do not match',
};

/**
 * 0. The query bitmap parameters are correct
 * 4. The query bitmap parameters are wrong
 * 5. Query bitmap failed
 */
export enum ReturnQueryFirmwareBitMap {
  Normal = 0x00,
  QueryBitmapParametersError = 0x04,
  QueryBitmapFailed = 0x05,
}
export const ReturnQueryFirmwareBitMapDescription = {
  [ReturnQueryFirmwareBitMap.Normal]: 'The query bitmap parameters are correct',
  [ReturnQueryFirmwareBitMap.QueryBitmapParametersError]: 'The query bitmap parameters are wrong',
  [ReturnQueryFirmwareBitMap.QueryBitmapFailed]: 'Query bitmap failed',
};

export enum SgpMasterDeviceType {
  PC = 0x00,
  UpperComputer = 0x01,
}

export enum SgpCommandCode {
  Handshake = 0x0000,
  GetFirmwareVersion = 0x10,
  StartFirmwareDownload = 0x11,
  DistributeFirmware = 0x12,
  ValidateFirmware = 0x13,
  QueryFirmwareBitMap = 0x14,
}

export class CancellationSignal {
  cancelled = false;
}

export interface SgpMetadataFirmwareType {
  firmwareName: string; // 20 bytes
  firmwareVersion: string; // 20 bytes
  firmwareSize: Uint8Array; // 4 bytes
  firmwareCRC32: Uint8Array; // 4 bytes
  firmwareEffect: Uint8Array; // 1 byte
  startSoftwareAddress: Uint8Array; // 4 bytes

  fwChecksum?: Uint8Array; // 4 bytes
  chipModel?: string; // 20 bytes
  hardwareVersion?: string; // 20 bytes
  buildDate?: string; // 12 bytes
  chipCode?: Uint8Array; // 2 byte
}

export interface SafetyMetadata {
  content: string;
  countryCode: number;
  countryName: string;
  dataLength: number;
  parsedContent: SafetyMetadataParsedContent;
  dataPointer: number;
  regionCode: number;
  regionIndex: number;
  regionName: string;
}

export type SafetyMetadataParsedContent = {
  [key: string]: number | {
    [key: string]: number[];
  };
};

export interface UpgradeState {
  upgradable: boolean;
  isUpdated: { [moduleName: string]: boolean } | null;
  upgradePossible: { [moduleName: string]: boolean } | null;
  upgradeLinks: { [moduleName: string]: string | null } | null;
  latestVersions: { [moduleName: string]: string } | null;
  safetyCustom: string[];
}

export interface SafetyDetails {
  country: string;
  countryCode: string;
  region: string;
  regionCode: string;
  version: string;
  index: number;
}

export enum UpdateResult {
  waiting = 'waiting',
  inProgress = 'inProgress',
  success = 'success',
  failed = 'failed',
  cancelled = 'cancelled',
}

export interface UpgradeMultiModulesProgress {
  name: string;
  progress: number;
  code: number;
}

export interface UpgradeMultiModules {
  modules: Array<{
    name: string;
    status: UpdateResult;
  }>;
  current: {
    name: string;
    status: UpdateResult;
  } | null;
  upgrade: UpgradeMultiModulesProgress | null;
  lastUpdated: Date;
}

export interface UpgradeWizardState {
  currentPhase: number;
  errorMessage: string | null;
  isUpdating: boolean;
  totalProgress: number;
  updatePhases: string[];
  upgradeConfig: UpgradeState | null;
  inverterUpgradeTypes: string[] | null;
  batteryUpgradeTypes: string[] | null;
  safetyMetadata: SafetyMetadata[] | null;
  selectedSafetyModel?: string;
  safetyModels?: string[];
  safetyList: SafetyDetails[];
  filteredSafety: SafetyDetails[];
  selectedSafety: string;
  selectedSafetyIndex: number | null;
  multiModules: UpgradeMultiModules | null;
}
